<template>
  <div class="ai-agent-setup flex min-h-screen flex-col overflow-x-hidden bg-base-200 font-sans lg:flex-row" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <aside class="w-full bg-base-100 border-b border-base-300 flex flex-col shadow-sm z-20 lg:w-72 lg:flex-shrink-0 lg:self-stretch lg:border-b-0 lg:border-r">
      <div class="p-6 border-b border-base-300 flex items-center justify-between">
        <div>
          <h1 class="font-bold text-xl tracking-tight text-primary">{{ isEditMode === true ? t('aiPhoneIvrSetup.title.edit', 'Edit AI Phone IVR') : t('aiPhoneIvrSetup.title.new', 'New AI Phone IVR') }}</h1>
          <div class="mt-1 text-xs text-base-content opacity-70">{{ t('aiPhoneIvrSetup.subtitle', 'Build a phone IVR setup with transfer routes and voice settings.') }}</div>
        </div>
        <button @click="router.push('/aiChatAgents')" class="btn btn-ghost btn-square btn-sm">
          <Icon icon="heroicons:x-mark" class="w-5 h-5" />
        </button>
      </div>
      <div class="px-4 pt-4">
        <select v-model="viewMode" class="select select-bordered select-sm w-full" @change="switchView($event.target.value)">
          <option value="full">{{ t('aiChatAgentSetup.setupType.regular', 'Regular') }}</option>
          <option value="light">{{ t('aiChatAgentSetup.setupType.light', 'Light') }}</option>
          <option value="ivr">{{ t('aiPhoneIvrSetup.setupType.ivr', 'AI Phone IVR') }}</option>
        </select>
      </div>
      <div class="flex-1 py-4">
        <ul class="menu w-full px-2 space-y-1">
          <li v-for="item in menuItems" :key="item.page">
            <a
              @click="currentPage = item.page"
              :class="[isRtl === true ? 'space-x-reverse' : '', { 'active': currentPage === item.page, 'bg-primary text-primary-content': currentPage === item.page }]"
              class="rounded-lg font-semibold px-3 py-2 transition-colors duration-150 flex items-center justify-start space-x-3 hover:bg-base-200"
            >
              <span
                class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-base-200 text-base-content/80 transition-colors duration-150"
                :class="currentPage === item.page ? 'bg-primary-content/25 text-primary-content ring-1 ring-primary-content/40 shadow-sm' : ''"
              >
                <Icon :icon="item.icon" class="w-5 h-5" />
              </span>
              <span class="truncate">{{ item.label }}</span>
            </a>
          </li>
        </ul>
      </div>
      <div class="p-4 border-t border-base-300 bg-base-50/50">
        <div class="text-xs text-base-content/70 mb-2">{{ t('aiPhoneIvrSetup.saveHint', 'Changes are saved into a generated IVR prompt behind the scenes.') }}</div>
        <div v-if="isEditMode === true" class="text-xs text-base-content/70 truncate font-medium">{{ agentName }}</div>
      </div>
    </aside>

    <main class="relative flex min-w-0 flex-1 flex-col overflow-visible bg-base-200/50">
      <div v-if="initializing === true" class="absolute inset-0 bg-base-100/80 z-50 flex items-center justify-center backdrop-blur-sm">
        <span class="loading loading-spinner loading-lg text-primary"></span>
      </div>

      <div class="border-b border-base-300 bg-base-100 flex flex-wrap items-center justify-between px-4 py-3 shadow-sm z-10 flex-shrink-0 sm:px-6 lg:sticky lg:top-0 lg:h-16 lg:flex-nowrap lg:px-8">
        <h2 class="text-lg font-semibold flex items-center space-x-2" :class="isRtl === true ? 'space-x-reverse' : ''">
          <span class="opacity-50 font-normal">{{ t('aiPhoneIvrSetup.breadcrumb.setup', 'Setup') }}</span>
          <span class="opacity-50">/</span>
          <span>{{ currentPageLabel }}</span>
        </h2>
        <div class="mt-3 flex w-full flex-wrap items-center justify-end sm:mt-0 sm:w-auto">
          <label class="label cursor-pointer" style="margin-inline-end: 12px;">
            <span class="label-text" style="margin-inline-end: 8px;">{{ t('aiPhoneIvrSetup.active', 'Active') }}</span>
            <input v-model="agentActive" type="checkbox" class="toggle toggle-primary" />
          </label>
          <button @click="saveAgent(false)" class="btn btn-sm btn-neutral" :disabled="isSaving === true">
            <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-xs" style="margin-inline-end: 6px;"></span>
            {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
          </button>
          <button @click="saveAgent(true)" class="btn btn-sm" :disabled="isSaving === true" style="margin-inline-start: 8px;">
            <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-xs" style="margin-inline-end: 6px;"></span>
            {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
          </button>
        </div>
      </div>

      <div class="flex-1 scroll-smooth overflow-visible p-4 sm:p-6 lg:p-8">
        <div class="mx-auto w-full max-w-6xl pb-8 lg:pb-20">
          <div v-show="currentPage === 1" class="bg-base-100 border border-base-300 rounded-box p-6 shadow-sm">
            <div class="flex items-center justify-between">
              <h3 class="text-2xl">{{ t('aiPhoneIvrSetup.tabs.description', 'Description') }}</h3>
            </div>
            <div class="mt-6">
              <div class="mb-5">
                <label class="label">
                  <span class="label-text">{{ t('aiChatAgentSetup.agentInformation.agentNameLabel', 'Agent Name') }}</span>
                </label>
                <input v-model="agentName" type="text" maxlength="100" class="input input-bordered w-full" :class="agentNameError !== '' ? 'input-error' : ''" />
                <div v-if="agentNameError !== ''" class="mt-1 text-xs text-error">{{ agentNameError }}</div>
              </div>
              <div class="mb-5">
                <label class="label">
                  <span class="label-text">{{ t('aiChatAgentSetup.agentInformation.agentShortDescriptionLabel', 'Agent Short Description') }}</span>
                </label>
                <textarea v-model="agentShortDescription" class="textarea textarea-bordered w-full" :class="agentShortDescriptionError !== '' ? 'textarea-error' : ''" style="min-height: 92px;" maxlength="250"></textarea>
                <div class="mt-1 text-xs text-base-content opacity-60">{{ agentShortDescription.length }}/250</div>
                <div v-if="agentShortDescriptionError !== ''" class="mt-1 text-xs text-error">{{ agentShortDescriptionError }}</div>
              </div>
              <div class="mb-5">
                <label class="label">
                  <span class="label-text">{{ t('aiPhoneIvrSetup.generalTransferSentence', 'General Transfer Sentence') }}</span>
                </label>
                <textarea v-model="generalTransferSentence" class="textarea textarea-bordered w-full" :class="generalTransferSentenceError !== '' ? 'textarea-error' : ''" style="min-height: 92px;"></textarea>
                <div class="mt-1 text-xs text-base-content opacity-60">{{ t('aiPhoneIvrSetup.generalTransferSentenceHint', 'This line is used before transfers unless a division route has its own sentence.') }}</div>
                <div v-if="generalTransferSentenceError !== ''" class="mt-1 text-xs text-error">{{ generalTransferSentenceError }}</div>
              </div>
              <div class="mb-5">
                <label class="label">
                  <span class="label-text font-semibold">{{ t('aiPhoneIvrSetup.settings.modeTitle', 'Inbound Connection Method') }}</span>
                </label>
                <div class="flex flex-wrap items-center" style="margin-bottom: 12px;">
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="connectionMode === 'sip' ? 'btn-neutral' : ''"
                    style="margin-inline-end: 8px; margin-bottom: 8px;"
                    @click="setConnectionMode('sip')"
                  >
                    <Icon icon="heroicons:bolt" class="w-4 h-4" style="margin-inline-end: 4px;" />
                    {{ t('aiPhoneIvrSetup.settings.modeSip', 'Direct SIP Connection (BYOT - You Control Phone)') }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="connectionMode === 'phone' ? 'btn-neutral' : ''"
                    style="margin-bottom: 8px;"
                    @click="setConnectionMode('phone')"
                  >
                    <Icon icon="heroicons:phone" class="w-4 h-4" style="margin-inline-end: 4px;" />
                    {{ t('aiPhoneIvrSetup.settings.modePhone', 'Assigned Phone Number') }}
                  </button>
                </div>

                <div v-if="connectionMode === 'sip'" class="rounded-xl border border-base-300 bg-base-200/50 p-4">
                  <div class="flex items-center justify-between">
                    <div>
                      <div class="font-semibold text-sm">{{ t('aiPhoneIvrSetup.settings.modeSip', 'Direct SIP Connection (BYOT - You Control Phone)') }}</div>
                      <div class="text-xs text-base-content/70 mt-1">{{ t('aiPhoneIvrSetup.settings.directSipActive', 'Direct SIP mode is active. Inbound calls route by Agent ID and phone number assignment is optional.') }}</div>
                    </div>
                    <button type="button" class="btn btn-sm" style="margin-inline-start: 12px;" @click="currentPage = 4">
                      {{ t('aiPhoneIvrSetup.tabs.settings', 'Settings') }}
                    </button>
                  </div>
                </div>

                <div v-if="connectionMode === 'phone'">
                  <CommunicationPhoneNumberSelect v-model:phoneSourceId="selectedPhoneSourceId" v-model:phoneNumber="phoneDisplayNumber" />
                  <div v-if="phoneSelectionError !== ''" class="mt-1 text-xs text-error">{{ phoneSelectionError }}</div>
                </div>
              </div>
              <IvrDivisionRoutesEditor v-model="ivrRoutes" />
              <div v-if="ivrRoutesError !== ''" class="mt-2 text-xs text-error">{{ ivrRoutesError }}</div>
            </div>
          </div>

          <div v-show="currentPage === 2" class="bg-base-100 border border-base-300 rounded-box p-6 shadow-sm">
            <div class="flex items-center justify-between">
              <h3 class="text-2xl">{{ t('aiChatAgentSetup.tabs.rules', 'Rules') }}</h3>
            </div>
            <div class="mt-4">
              <RuleLibraryPanel :is-rtl="isRtl" :existing-rules="rules" :selected-language="agentLanguage" :agent-description="agentShortDescription" @add-rule="addRuleFromLibrary" />
            </div>
            <div class="mt-4 flex items-start">
              <textarea v-model="newRule" maxlength="3500" rows="2" class="textarea textarea-bordered flex-1" :placeholder="t('aiChatAgentSetup.rules.newRulePlaceholder','Enter a new rule')"></textarea>
              <button @click="addRule()" class="btn" style="margin-inline-start: 8px;">{{ t('aiChatAgentSetup.rules.addRuleButton','Add Rule') }}</button>
            </div>
            <div class="mt-4">
              <div v-if="rules.length === 0" class="rounded-xl border border-dashed border-base-300 bg-base-200/40 px-4 py-6 text-sm text-base-content opacity-70">{{ t('aiPhoneIvrSetup.rules.empty', 'No extra rules added yet.') }}</div>
              <div v-else>
                <div v-for="(rule, index) in rules" :key="`${index}-${rule}`" class="rounded-xl border border-base-300 bg-base-50 p-4" :style="index > 0 ? 'margin-top: 12px;' : ''">
                  <textarea v-model="rules[index]" maxlength="3500" rows="3" class="textarea textarea-bordered w-full"></textarea>
                  <div class="mt-3 flex justify-end">
                    <button @click="removeRule(index)" class="btn btn-sm btn-error">{{ t('aiChatAgentSetup.rules.removeRuleButton','Remove') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-show="currentPage === 3" class="bg-base-100 border border-base-300 rounded-box p-6 shadow-sm">
            <div class="flex items-center justify-between">
              <h3 class="text-2xl">{{ t('aiChatAgentSetup.tabs.voiceSettings', 'Voice Settings') }}</h3>
            </div>
            <div class="mt-6">
              <VoiceSettingsFields
                :voiceType="voiceSettingsVoiceType"
                :vadEngine="voiceSettingsVadEngine"
                :selectedVoiceId="voiceSettingsVoiceId"
                :accent="voiceSettingsAccent"
                :language="voiceSettingsLanguage"
                :languageOptions="voiceLanguageOptions"
                :selectedTermsPackages="voiceSettingsTermsPackages"
                :speed="voiceSettingsSpeed"
                :customPacingEnabled="voiceSettingsCustomPacingEnabled"
                :pacing="voiceSettingsPacing"
                :durationStretch="voiceSettingsDurationStretch"
                :useDashPauses="voiceSettingsUseDashPauses"
                :pauseEveryWords="voiceSettingsPauseEveryWords"
                :dashPauseMs="voiceSettingsDashPauseMs"
                :commaPauseMs="voiceSettingsCommaPauseMs"
                :periodPauseMs="voiceSettingsPeriodPauseMs"
                :idleAutoResponseEnabled="voiceSettingsIdleAutoResponseEnabled"
                :startingMessageIdleAutoResponseSeconds="voiceSettingsStartingMessageIdleAutoResponseSeconds"
                :idleAutoResponseSeconds="voiceSettingsIdleAutoResponseSeconds"
                :toolUseAutoResponseSeconds="voiceSettingsToolUseAutoResponseSeconds"
                :tone="voiceSettingsTone"
                :toneOptions="voiceToneOptions"
                :mode="voiceSettingsMode"
                :modeOptions="voiceModeOptions"
                :cancellingMode="voiceSettingsCancellingMode"
                :cancellingModeOptions="voiceCancellingModeOptions"
                :audioProcessorEngine="voiceSettingsAudioProcessorEngine"
                :audioProcessorEngineOptions="voiceAudioProcessorEngineOptions"
                :isRtl="isRtl"
                :hideProvider="true"
                @update:voiceType="voiceSettingsVoiceType = $event"
                @update:vadEngine="voiceSettingsVadEngine = $event"
                @update:selectedVoiceId="voiceSettingsVoiceId = $event"
                @update:accent="voiceSettingsAccent = $event"
                @update:language="voiceSettingsLanguage = $event"
                @update:selectedTermsPackages="voiceSettingsTermsPackages = $event"
                @update:selectedTerms="voiceSettingsSonioxTerms = $event"
                @update:speed="voiceSettingsSpeed = $event"
                @update:customPacingEnabled="voiceSettingsCustomPacingEnabled = $event"
                @update:pacing="voiceSettingsPacing = $event"
                @update:durationStretch="voiceSettingsDurationStretch = $event"
                @update:useDashPauses="voiceSettingsUseDashPauses = $event"
                @update:pauseEveryWords="voiceSettingsPauseEveryWords = $event"
                @update:dashPauseMs="voiceSettingsDashPauseMs = $event"
                @update:commaPauseMs="voiceSettingsCommaPauseMs = $event"
                @update:periodPauseMs="voiceSettingsPeriodPauseMs = $event"
                @update:idleAutoResponseEnabled="voiceSettingsIdleAutoResponseEnabled = $event"
                @update:startingMessageIdleAutoResponseSeconds="voiceSettingsStartingMessageIdleAutoResponseSeconds = $event"
                @update:idleAutoResponseSeconds="voiceSettingsIdleAutoResponseSeconds = $event"
                @update:toolUseAutoResponseSeconds="voiceSettingsToolUseAutoResponseSeconds = $event"
                @update:tone="voiceSettingsTone = $event"
                @update:mode="voiceSettingsMode = $event"
                @update:cancellingMode="voiceSettingsCancellingMode = $event"
                @update:audioProcessorEngine="voiceSettingsAudioProcessorEngine = $event"
              />
            </div>
          </div>

          <div v-show="currentPage === 4" class="bg-base-100 border border-base-300 rounded-box p-6 shadow-sm">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-2xl">{{ t('aiPhoneIvrSetup.settings.title', 'SIP Connection & Inbound Routing') }}</h3>
                <div class="mt-1 text-xs text-base-content opacity-70">{{ t('aiPhoneIvrSetup.settings.subtitle', 'Configure direct SIP connection using only the Agent ID. You control the phone numbers and telephony system.') }}</div>
              </div>
            </div>

            <div class="mt-6">
              <div class="rounded-xl border border-base-300 bg-base-200/40 p-4 mb-6">
                <div class="font-semibold text-sm">{{ t('aiPhoneIvrSetup.settings.callerControlTitle', 'Caller ID & Telephony Control') }}</div>
                <div class="text-xs text-base-content/70 mt-1 leading-relaxed">{{ t('aiPhoneIvrSetup.settings.callerControlDesc', 'Your PBX maintains full control of the caller identity. Send the caller phone number in From or P-Asserted-Identity, and the agent will preserve it for the conversation, caller history, and follow-ups.') }}</div>
              </div>

              <div class="mb-6">
                <label class="label">
                  <span class="label-text font-semibold">{{ t('aiPhoneIvrSetup.settings.modeTitle', 'Inbound Connection Method') }}</span>
                </label>
                <div class="grid grid-cols-1 md:grid-cols-2" style="margin-top: 4px;">
                  <div
                    class="rounded-xl border p-4 cursor-pointer transition-all"
                    :class="connectionMode === 'sip' ? 'border-primary bg-primary/5 shadow-sm' : 'border-base-300 hover:border-base-content/30'"
                    style="margin-inline-end: 8px; margin-bottom: 8px;"
                    @click="setConnectionMode('sip')"
                  >
                    <div class="flex items-center justify-between">
                      <div class="font-semibold text-sm">{{ t('aiPhoneIvrSetup.settings.modeSip', 'Direct SIP Connection (BYOT - You Control Phone)') }}</div>
                      <span v-if="connectionMode === 'sip'" class="badge badge-sm badge-neutral">{{ t('aiPhoneIvrSetup.active', 'Active') }}</span>
                    </div>
                    <div class="text-xs text-base-content/70 mt-2">{{ t('aiPhoneIvrSetup.settings.modeSipDesc', 'Route inbound calls from your PBX or carrier directly by Agent ID. No dedicated phone number required on Sonarix.') }}</div>
                  </div>

                  <div
                    class="rounded-xl border p-4 cursor-pointer transition-all"
                    :class="connectionMode === 'phone' ? 'border-primary bg-primary/5 shadow-sm' : 'border-base-300 hover:border-base-content/30'"
                    style="margin-bottom: 8px;"
                    @click="setConnectionMode('phone')"
                  >
                    <div class="flex items-center justify-between">
                      <div class="font-semibold text-sm">{{ t('aiPhoneIvrSetup.settings.modePhone', 'Assigned Phone Number') }}</div>
                      <span v-if="connectionMode === 'phone'" class="badge badge-sm badge-neutral">{{ t('aiPhoneIvrSetup.active', 'Active') }}</span>
                    </div>
                    <div class="text-xs text-base-content/70 mt-2">{{ t('aiPhoneIvrSetup.settings.modePhoneDesc', 'Use a phone number provisioned and managed on Sonarix.') }}</div>
                  </div>
                </div>
              </div>

              <div class="rounded-xl border border-base-300 p-5 mb-6">
                <h4 class="font-semibold text-base mb-4">{{ t('aiPhoneIvrSetup.settings.connectionDetails', 'SIP Connection Credentials') }}</h4>

                <div class="mb-4" v-if="currentAgentId !== ''">
                  <label class="label">
                    <span class="label-text">{{ t('aiPhoneIvrSetup.settings.agentId', 'Agent ID') }}</span>
                  </label>
                  <div class="flex items-center">
                    <input :value="currentAgentId" type="text" class="input input-bordered w-full font-mono text-sm" readonly />
                    <button type="button" class="btn btn-sm" style="margin-inline-start: 8px;" @click="copyValue(currentAgentId, t('aiPhoneIvrSetup.settings.agentId', 'Agent ID'))">
                      <Icon icon="heroicons:clipboard-document" class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="label">
                    <span class="label-text">{{ t('aiPhoneIvrSetup.settings.sipUri', 'Direct SIP URI') }}</span>
                  </label>
                  <div class="flex items-center">
                    <input :value="sipUri" type="text" class="input input-bordered w-full font-mono text-sm" readonly />
                    <button type="button" class="btn btn-sm" style="margin-inline-start: 8px;" @click="copyValue(sipUri, t('aiPhoneIvrSetup.settings.sipUri', 'Direct SIP URI'))">
                      <Icon icon="heroicons:clipboard-document" class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="label">
                    <span class="label-text">{{ t('aiPhoneIvrSetup.settings.sipUriAlt', 'Alternative SIP URI') }}</span>
                  </label>
                  <div class="flex items-center">
                    <input :value="sipUriAlt" type="text" class="input input-bordered w-full font-mono text-sm" readonly />
                    <button type="button" class="btn btn-sm" style="margin-inline-start: 8px;" @click="copyValue(sipUriAlt, t('aiPhoneIvrSetup.settings.sipUriAlt', 'Alternative SIP URI'))">
                      <Icon icon="heroicons:clipboard-document" class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div class="mb-4">
                  <label class="label">
                    <span class="label-text">{{ t('aiPhoneIvrSetup.settings.sipHeader', 'Custom Header for Trunk Routing') }}</span>
                  </label>
                  <div class="flex items-center">
                    <input :value="sipCustomHeader" type="text" class="input input-bordered w-full font-mono text-sm" readonly />
                    <button type="button" class="btn btn-sm" style="margin-inline-start: 8px;" @click="copyValue(sipCustomHeader, t('aiPhoneIvrSetup.settings.sipHeader', 'Custom Header'))">
                      <Icon icon="heroicons:clipboard-document" class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3" style="margin-top: 16px;">
                  <div class="border border-base-200 rounded-lg p-3 bg-base-50" style="margin-inline-end: 8px; margin-bottom: 8px;">
                    <div class="text-xs text-base-content/60">{{ t('aiPhoneIvrSetup.settings.sipHost', 'SIP Host / Proxy') }}</div>
                    <div class="font-mono text-sm font-semibold mt-1">sip.sonarix.ai</div>
                    <div class="text-xs text-base-content/50 mt-1">34.165.92.143</div>
                  </div>
                  <div class="border border-base-200 rounded-lg p-3 bg-base-50" style="margin-inline-end: 8px; margin-bottom: 8px;">
                    <div class="text-xs text-base-content/60">{{ t('aiPhoneIvrSetup.settings.sipPort', 'SIP Port') }}</div>
                    <div class="font-mono text-sm font-semibold mt-1">5060 / 5062</div>
                    <div class="text-xs text-base-content/50 mt-1">UDP / TCP</div>
                  </div>
                  <div class="border border-base-200 rounded-lg p-3 bg-base-50" style="margin-bottom: 8px;">
                    <div class="text-xs text-base-content/60">{{ t('aiPhoneIvrSetup.settings.sipCodecs', 'Supported Audio Codecs') }}</div>
                    <div class="font-mono text-sm font-semibold mt-1">PCMU, PCMA</div>
                    <div class="text-xs text-base-content/50 mt-1">RFC 2833 / 4733 DTMF</div>
                  </div>
                </div>
              </div>

              <div class="rounded-xl border border-base-300 p-5 mb-6">
                <h4 class="font-semibold text-base mb-4">{{ t('aiPhoneIvrSetup.settings.instructionsTitle', 'How to Connect Your PBX or Telephony System') }}</h4>

                <div class="rounded-lg border border-base-200 bg-base-50 p-4 mb-4">
                  <div class="font-semibold text-sm">{{ t('aiPhoneIvrSetup.settings.step1Title', 'Option 1: Direct SIP URI Dialing') }}</div>
                  <div class="text-xs text-base-content/70 mt-1 leading-relaxed">{{ t('aiPhoneIvrSetup.settings.step1Desc', 'Configure your PBX to send the SIP INVITE directly to the agent SIP URI. The agent is identified by the user portion of the SIP URI.') }}</div>
                </div>

                <div class="rounded-lg border border-base-200 bg-base-50 p-4 mb-4">
                  <div class="font-semibold text-sm">{{ t('aiPhoneIvrSetup.settings.step2Title', 'Option 2: SIP Trunk with Custom Header') }}</div>
                  <div class="text-xs text-base-content/70 mt-1 leading-relaxed">{{ t('aiPhoneIvrSetup.settings.step2Desc', 'If routing through a SIP trunk, route calls to sip.sonarix.ai:5060 and include the header X-Agent-ID with the Agent ID.') }}</div>
                </div>

                <div class="rounded-lg border border-base-200 bg-base-50 p-4">
                  <div class="flex items-center justify-between mb-2">
                    <span class="font-semibold text-sm">{{ t('aiPhoneIvrSetup.settings.asteriskExample', 'Asterisk / FreePBX Configuration Example') }}</span>
                    <button type="button" class="btn btn-sm" @click="copyValue(asteriskSnippet, t('aiPhoneIvrSetup.settings.asteriskExample', 'Asterisk Example'))">
                      <Icon icon="heroicons:clipboard-document" class="w-4 h-4" />
                    </button>
                  </div>
                  <textarea :value="asteriskSnippet" class="textarea textarea-bordered w-full font-mono text-xs" rows="3" readonly></textarea>
                </div>
              </div>

              <div class="rounded-xl border border-base-300 p-5">
                <h4 class="font-semibold text-base mb-4">{{ t('aiPhoneIvrSetup.settings.title', 'SIP Connection & Inbound Routing') }}</h4>
                <div class="form-control">
                  <label class="label cursor-pointer justify-start">
                    <input v-model="sipAutoAnswer" type="checkbox" class="toggle toggle-sm toggle-primary" style="margin-inline-end: 12px;" />
                    <div>
                      <div class="font-semibold text-sm">{{ t('aiPhoneIvrSetup.settings.autoAnswer', 'Auto-Answer Immediately (200 OK)') }}</div>
                      <div class="text-xs text-base-content/70 mt-0.5">{{ t('aiPhoneIvrSetup.settings.autoAnswerDesc', 'When enabled, Sonarix answers the call immediately. When disabled, 180 Ringing is sent prior to answer.') }}</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Icon } from '@iconify/vue/dist/offline';
import { useToast } from 'vue-toastification';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import VoiceSettingsFields from '@/components/VoiceSettingsFields.vue';
import RuleLibraryPanel from '@/components/RuleLibraryPanel.vue';
import CommunicationPhoneNumberSelect from '@/components/CommunicationPhoneNumberSelect.vue';
import IvrDivisionRoutesEditor from '@/components/IvrDivisionRoutesEditor.vue';
import { defaultVoiceId } from '@/utils/voiceCatalog.js';
import { geminiLanguageNames } from '@/utils/geminiLanguages.js';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { t, currentLanguage } = useI18n();

const apiEndpoint = reactive(globalState.apiEndpoint);
const initializing = ref(true);
const isSaving = ref(false);
const isSavingAndRedirecting = ref(false);
const currentPage = ref(1);
const viewMode = ref('ivr');
const loadedAgentRecord = ref(null);
const preservedDivisionIds = ref([]);
const preservedAgentType = ref('PublicAgent');
const preservedAgentMode = ref('Agentic');
const preservedAgentLanguage = ref('auto');
const agentActive = ref(true);
const agentName = ref('');
const agentShortDescription = ref('');
const agentLanguage = ref('auto');
const generalTransferSentence = ref('');
const ivrRoutes = ref([]);
const selectedPhoneSourceId = ref(null);
const phoneDisplayNumber = ref('');
const rules = ref([]);
const newRule = ref('');
const agentNameError = ref('');
const agentShortDescriptionError = ref('');
const generalTransferSentenceError = ref('');
const phoneSelectionError = ref('');
const ivrRoutesError = ref('');

const connectionMode = ref('sip');
const sipAutoAnswer = ref(true);
const sipHost = ref('sip.sonarix.ai');
const sipPort = ref('5060');

const currentAgentId = computed(() => {
  if (typeof route.query.edit === 'string' && route.query.edit.trim().length > 0) {
    return route.query.edit.trim();
  }
  return '';
});

const sipUri = computed(() => {
  if (currentAgentId.value !== '') {
    return `sip:${currentAgentId.value}@${sipHost.value}:${sipPort.value}`;
  }
  return `sip:<agentId>@${sipHost.value}:${sipPort.value}`;
});

const sipUriAlt = computed(() => {
  if (currentAgentId.value !== '') {
    return `sip:agent_${currentAgentId.value}@${sipHost.value}:${sipPort.value}`;
  }
  return `sip:agent_<agentId>@${sipHost.value}:${sipPort.value}`;
});

const sipCustomHeader = computed(() => {
  if (currentAgentId.value !== '') {
    return `X-Agent-ID: ${currentAgentId.value}`;
  }
  return 'X-Agent-ID: <agentId>';
});

const asteriskSnippet = computed(() => {
  const idVal = currentAgentId.value !== '' ? currentAgentId.value : '<agentId>';
  return `exten => _X.,1,Set(PJSIP_HEADER(add,X-Agent-ID)=${idVal})\nsame => n,Dial(PJSIP/${sipHost.value}:${sipPort.value})`;
});

function setConnectionMode(mode) {
  if (mode === 'sip') {
    connectionMode.value = 'sip';
    phoneSelectionError.value = '';
    return;
  }
  connectionMode.value = 'phone';
}

async function copyValue(val, label) {
  if (typeof val !== 'string' || val.trim() === '') {
    return;
  }
  try {
    await navigator.clipboard.writeText(val.trim());
    toast.success(`${label} ${t('aiPhoneIvrSetup.copied', 'Copied to clipboard')}`);
  } catch (e) {
    toast.info(`${label}: ${val}`);
  }
}

const voiceSettingsVoiceType = ref('vtv');
const voiceSettingsVadEngine = ref('tiger');
const voiceSettingsVoiceId = ref(defaultVoiceId);
const voiceSettingsAccent = ref('Israeli');
const voiceSettingsLanguage = ref('English');
const voiceSettingsSpeed = ref(1);
const voiceSettingsCustomPacingEnabled = ref(false);
const voiceSettingsPacing = ref(1.05);
const voiceSettingsDurationStretch = ref(1);
const voiceSettingsUseDashPauses = ref(true);
const voiceSettingsPauseEveryWords = ref(0);
const voiceSettingsDashPauseMs = ref(180);
const voiceSettingsCommaPauseMs = ref(120);
const voiceSettingsPeriodPauseMs = ref(260);
const voiceSettingsIdleAutoResponseEnabled = ref(true);
const voiceSettingsStartingMessageIdleAutoResponseSeconds = ref(6);
const voiceSettingsIdleAutoResponseSeconds = ref(3);
const voiceSettingsToolUseAutoResponseSeconds = ref(2);
const voiceSettingsTone = ref('Neutral');
const voiceSettingsMode = ref('interruptible');
const voiceSettingsCancellingMode = ref('fast');
const voiceSettingsAudioProcessorEngine = ref('quail');
const voiceSettingsTermsPackages = ref([]);
const voiceSettingsSonioxTerms = ref([]);
const voiceLanguageOptions = geminiLanguageNames;
const voiceToneOptions = ['Neutral', 'Friendly', 'Formal', 'Enthusiastic', 'Calm'];
const voiceModeOptions = [
  { id: 'interruptible', label: 'Talk and interrupt' },
  { id: 'wait full', label: 'Wait for full answer' }
];
const voiceCancellingModeOptions = [
  { id: 'fast', label: 'Fast Mode' },
  { id: 'quality', label: 'Quality Mode' }
];
const voiceAudioProcessorEngineOptions = [
  { id: 'quail', label: 'Quail VF' },
  { id: 'internal', label: 'Genovise' },
  { id: 'external', label: 'External engine' }
];

const menuItems = computed(() => [
  { page: 1, label: t('aiPhoneIvrSetup.tabs.description', 'Description'), icon: 'heroicons:document-text' },
  { page: 2, label: t('aiChatAgentSetup.tabs.rules', 'Rules'), icon: 'heroicons:clipboard-document-list' },
  { page: 3, label: t('aiChatAgentSetup.tabs.voiceSettings', 'Voice Settings'), icon: 'heroicons:microphone' },
  { page: 4, label: t('aiPhoneIvrSetup.tabs.settings', 'Settings'), icon: 'heroicons:cog-6-tooth' }
]);

const currentPageLabel = computed(() => {
  const items = menuItems.value;
  for (let index = 0; index < items.length; index = index + 1) {
    if (items[index].page === currentPage.value) {
      return items[index].label;
    }
  }
  return items[0].label;
});

const isEditMode = computed(() => typeof route.query.edit === 'string' && route.query.edit.trim().length > 0);
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);

function getAuthToken() {
  if (globalState.authUsesHttpOnlyCookies === true) {
    return '';
  }
  const authRow = document.cookie.split('; ').find((row) => row.startsWith('authToken=') === true);
  const jwtRow = document.cookie.split('; ').find((row) => row.startsWith('jwtToken=') === true);
  const authToken = authRow !== undefined ? authRow.split('=')[1] : '';
  const jwtToken = jwtRow !== undefined ? jwtRow.split('=')[1] : '';
  if (authToken !== '' && authToken !== undefined && authToken !== null) {
    return authToken;
  }
  if (jwtToken !== '' && jwtToken !== undefined && jwtToken !== null) {
    return jwtToken;
  }
  return '';
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function getSecuredAxiosConfig(extraConfig) {
  const headers = getAuthHeaders();
  const cfg = extraConfig !== undefined && extraConfig !== null && typeof extraConfig === 'object' ? { ...extraConfig } : {};
  cfg.headers = headers;
  const authHeader = headers.Authorization;
  if (typeof authHeader !== 'string' || authHeader.trim().length === 0) {
    cfg.withCredentials = true;
  }
  return cfg;
}

function encodePathSegment(value) {
  const raw = value !== undefined && value !== null ? String(value) : '';
  return encodeURIComponent(raw);
}

function handleApiError(error, context) {
  console.error(`Error during ${context}:`, error);
  const errorMessage = typeof error?.response?.data?.message === 'string' ? error.response.data.message : (typeof error?.response?.data?.error === 'string' ? error.response.data.error : '');
  const lowerMessage = typeof errorMessage === 'string' ? errorMessage.toLowerCase() : '';
  const isExpired = lowerMessage.includes('jwt expired') === true;
  if (error?.response?.status === 401 || error?.response?.status === 403 || isExpired === true) {
    toast.error(t('common.authError', 'Authentication error. Redirecting to login.'));
    router.push('/login');
    return;
  }
  if (errorMessage !== '') {
    toast.error(errorMessage);
    return;
  }
  toast.error(t('common.genericError', 'An unexpected error occurred.'));
}

function normalizeSetupType(value) {
  if (value === 'full' || value === 'light' || value === 'ivr') {
    return value;
  }
  return 'ivr';
}

function switchView(selectedMode) {
  const nextMode = normalizeSetupType(selectedMode);
  viewMode.value = nextMode;
  if (nextMode === 'full') {
    router.push({ path: '/aiChatAgentSetup', query: { ...route.query, setupType: 'full' } });
    return;
  }
  if (nextMode === 'light') {
    router.push({ path: '/aiChatAgentSetupLight', query: { ...route.query, setupType: 'light' } });
    return;
  }
  if (route.path !== '/aiPhoneIvrSetup') {
    router.push({ path: '/aiPhoneIvrSetup', query: { ...route.query, setupType: 'ivr' } });
  }
}

function normalizeVoiceSettingsCancellingMode(value) {
  if (typeof value !== 'string') {
    return 'fast';
  }
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (normalized === 'quality' || normalized === 'qualitymode') {
    return 'quality';
  }
  return 'fast';
}

function normalizeVoiceSettingsVoiceType(value) {
  if (typeof value !== 'string') {
    return 'vtv';
  }
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (normalized === 'hybrid') {
    return 'hybrid';
  }
  if (normalized === 'vtvwithtts') {
    return 'hybrid';
  }
  if (normalized === 'realtimewithtts') {
    return 'hybrid';
  }
  if (normalized === 'vllm') {
    return 'vllm';
  }
  if (normalized === 'realtimewithcustomsttopenai' || normalized === 'openairealtimecustomstt') {
    return 'realtimeWithCustomSTTOpenAI';
  }
  if (normalized === 'realtimewithcustomsttdefault') {
    return 'realtimeWithCustomSTTDefault';
  }
  return 'vtv';
}

function normalizeVoiceSettingsVadEngine(value) {
  if (typeof value !== 'string') {
    return 'tiger';
  }
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (normalized === 'flyingbird') {
    return 'flyingBird';
  }
  return 'tiger';
}

function normalizeVoiceSettingsAudioProcessorEngine(value) {
  if (typeof value !== 'string') {
    return 'quail';
  }
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (normalized === 'quail' || normalized === 'quailvf' || normalized === 'aic' || normalized === 'aicoustics') {
    return 'quail';
  }
  if (normalized === 'internal' || normalized === 'internalengine' || normalized === 'genovise' || normalized === 'genoviseengine') {
    return 'internal';
  }
  if (normalized === 'external' || normalized === 'externalengine' || normalized === 'krisp' || normalized === 'krispvoicefocus') {
    return 'external';
  }
  return 'quail';
}

function createRouteId() {
  return `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
}

function normalizeRoute(item) {
  const source = item !== null && item !== undefined && typeof item === 'object' ? item : {};
  return {
    id: typeof source.id === 'string' && source.id.trim() !== '' ? source.id : createRouteId(),
    divisionName: typeof source.divisionName === 'string' ? source.divisionName.trim() : '',
    transferDescription: typeof source.transferDescription === 'string' ? source.transferDescription.trim() : '',
    transferSentence: typeof source.transferSentence === 'string' ? source.transferSentence.trim() : '',
    sipPort: typeof source.sipPort === 'string' ? source.sipPort.trim() : String(source.sipPort !== undefined && source.sipPort !== null ? source.sipPort : '').trim(),
    ip: typeof source.ip === 'string' ? source.ip.trim() : '',
    phoneNumber: typeof source.phoneNumber === 'string' ? source.phoneNumber.trim() : ''
  };
}

function getIsraeliPhoneError(value) {
  const rawValue = typeof value === 'string' ? value.trim() : '';
  if (rawValue === '') {
    return '';
  }
  const compactValue = rawValue.replace(/\s+/g, '');
  const digitsOnly = compactValue.replace(/[^\d]/g, '');
  if (compactValue.startsWith('+972') === true || compactValue.startsWith('972') === true) {
    return t('aiPhoneIvrSetup.routes.israeliNumberError', 'Israeli phone numbers must start with 0.');
  }
  if (digitsOnly.length === 9 && compactValue.startsWith('0') !== true) {
    return t('aiPhoneIvrSetup.routes.israeliNumberError', 'Israeli phone numbers must start with 0.');
  }
  return '';
}

function clearErrors() {
  agentNameError.value = '';
  agentShortDescriptionError.value = '';
  generalTransferSentenceError.value = '';
  phoneSelectionError.value = '';
  ivrRoutesError.value = '';
}

function addRule() {
  const nextRule = typeof newRule.value === 'string' ? newRule.value.trim() : '';
  if (nextRule === '') {
    return;
  }
  rules.value.push(nextRule);
  newRule.value = '';
}

function addRuleFromLibrary(ruleText) {
  const nextRule = typeof ruleText === 'string' ? ruleText.trim() : '';
  if (nextRule === '') {
    return;
  }
  if (rules.value.includes(nextRule) === false) {
    rules.value.push(nextRule);
  }
}

function removeRule(index) {
  if (index >= 0 && index < rules.value.length) {
    rules.value.splice(index, 1);
  }
}

function buildGeneratedDescription() {
  const lines = [];
  lines.push(`You are ${agentName.value.trim()}, an AI phone IVR agent.`);
  if (agentShortDescription.value.trim() !== '') {
    lines.push(`Purpose: ${agentShortDescription.value.trim()}`);
  }
  const normVoiceLang = typeof voiceSettingsLanguage.value === 'string' ? voiceSettingsLanguage.value.trim().toLowerCase() : '';
  if (normVoiceLang.indexOf('auto') === 0) {
    lines.push('Language mode: Auto-detect language with automatic switching is active. Detect the language spoken by the caller in each turn and converse in that exact same language. If the caller switches languages, immediately switch your response language to match.');
  }
  lines.push('Identify the caller intent quickly and route the call to the most relevant division.');
  lines.push('Keep answers short, clear, and suitable for voice calls.');
  lines.push(`Before each transfer, say this sentence unless the matched division has its own sentence: ${generalTransferSentence.value.trim()}`);
  lines.push('Use the following transfer routes exactly as configured:');
  for (let index = 0; index < ivrRoutes.value.length; index = index + 1) {
    const routeItem = normalizeRoute(ivrRoutes.value[index]);
    const routeSentence = routeItem.transferSentence !== '' ? routeItem.transferSentence : generalTransferSentence.value.trim();
    lines.push(`${index + 1}. Division: ${routeItem.divisionName}`);
    lines.push(`Transfer when: ${routeItem.transferDescription}`);
    lines.push(`Sentence before transfer: ${routeSentence}`);
    lines.push(`Transfer target SIP port: ${routeItem.sipPort}`);
    lines.push(`Transfer target IP: ${routeItem.ip}`);
    lines.push(`Transfer target phone number: ${routeItem.phoneNumber}`);
  }
  lines.push('If the caller request does not match any route, explain that no direct transfer path is available and collect the request carefully.');
  return lines.join('\n');
}

function validateForm() {
  clearErrors();
  if (agentName.value.trim() === '') {
    agentNameError.value = t('aiPhoneIvrSetup.validation.agentName', 'Agent name is required.');
  }
  if (agentShortDescription.value.trim() === '') {
    agentShortDescriptionError.value = t('aiPhoneIvrSetup.validation.shortDescription', 'Short description is required.');
  }
  if (generalTransferSentence.value.trim() === '') {
    generalTransferSentenceError.value = t('aiPhoneIvrSetup.validation.generalTransferSentence', 'General transfer sentence is required.');
  }
  if (connectionMode.value !== 'sip') {
    const isInvalidSource = [selectedPhoneSourceId.value === null, Number.isFinite(Number(selectedPhoneSourceId.value)) !== true].includes(true);
    if (isInvalidSource === true) {
      phoneSelectionError.value = t('aiPhoneIvrSetup.validation.phoneSelection', 'Choose a communication phone number.');
    }
  }
  if (Array.isArray(ivrRoutes.value) !== true || ivrRoutes.value.length === 0) {
    ivrRoutesError.value = t('aiPhoneIvrSetup.validation.routesRequired', 'Add at least one division route.');
  } else {
    for (let index = 0; index < ivrRoutes.value.length; index = index + 1) {
      const routeItem = normalizeRoute(ivrRoutes.value[index]);
      if (routeItem.divisionName === '' || routeItem.transferDescription === '' || routeItem.sipPort === '' || routeItem.ip === '' || routeItem.phoneNumber === '') {
        ivrRoutesError.value = t('aiPhoneIvrSetup.validation.routeFields', 'Each division route must include a name, transfer description, SIP port, IP, and phone number.');
        break;
      }
      if (getIsraeliPhoneError(routeItem.phoneNumber) !== '') {
        ivrRoutesError.value = getIsraeliPhoneError(routeItem.phoneNumber);
        break;
      }
    }
  }
  return agentNameError.value === '' && agentShortDescriptionError.value === '' && generalTransferSentenceError.value === '' && phoneSelectionError.value === '' && ivrRoutesError.value === '';
}

function getCurrentAgentData() {
  const stored = loadedAgentRecord.value !== null && loadedAgentRecord.value !== undefined && typeof loadedAgentRecord.value.agentdata === 'object' && loadedAgentRecord.value.agentdata !== null ? loadedAgentRecord.value.agentdata : {};
  return stored;
}

function buildVoiceSettingsPayload() {
  return {
    voiceType: normalizeVoiceSettingsVoiceType(voiceSettingsVoiceType.value),
    vadEngine: normalizeVoiceSettingsVadEngine(voiceSettingsVadEngine.value),
    voiceId: typeof voiceSettingsVoiceId.value === 'string' && voiceSettingsVoiceId.value.trim() !== '' ? voiceSettingsVoiceId.value : defaultVoiceId,
    accent: voiceSettingsAccent.value,
    language: voiceSettingsLanguage.value,
    speed: Number.isFinite(Number(voiceSettingsSpeed.value)) === true ? Number(voiceSettingsSpeed.value) : 1,
    customPacingEnabled: voiceSettingsCustomPacingEnabled.value === true,
    pacing: Number.isFinite(Number(voiceSettingsPacing.value)) === true ? Number(voiceSettingsPacing.value) : 1.05,
    durationStretch: Number.isFinite(Number(voiceSettingsDurationStretch.value)) === true ? Number(voiceSettingsDurationStretch.value) : 1,
    useDashPauses: voiceSettingsUseDashPauses.value === true,
    pauseEveryWords: Number.isFinite(Number(voiceSettingsPauseEveryWords.value)) === true ? Number(voiceSettingsPauseEveryWords.value) : 0,
    dashPauseMs: Number.isFinite(Number(voiceSettingsDashPauseMs.value)) === true ? Number(voiceSettingsDashPauseMs.value) : 180,
    commaPauseMs: Number.isFinite(Number(voiceSettingsCommaPauseMs.value)) === true ? Number(voiceSettingsCommaPauseMs.value) : 120,
    periodPauseMs: Number.isFinite(Number(voiceSettingsPeriodPauseMs.value)) === true ? Number(voiceSettingsPeriodPauseMs.value) : 260,
    idleAutoResponseEnabled: voiceSettingsIdleAutoResponseEnabled.value === true,
    startingMessageIdleAutoResponseSeconds: Number.isFinite(Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value)) === true && Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value) >= 1 ? Math.min(300, Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value)) : 6,
    idleAutoResponseSeconds: Number.isFinite(Number(voiceSettingsIdleAutoResponseSeconds.value)) === true && Number(voiceSettingsIdleAutoResponseSeconds.value) >= 1 ? Math.min(300, Number(voiceSettingsIdleAutoResponseSeconds.value)) : 3,
    toolUseAutoResponseSeconds: Number.isFinite(Number(voiceSettingsToolUseAutoResponseSeconds.value)) === true && Number(voiceSettingsToolUseAutoResponseSeconds.value) >= 0 ? Math.min(300, Number(voiceSettingsToolUseAutoResponseSeconds.value)) : 2,
    tone: voiceSettingsTone.value,
    mode: voiceSettingsMode.value,
    cancellingMode: normalizeVoiceSettingsCancellingMode(voiceSettingsCancellingMode.value),
    audioProcessorEngine: normalizeVoiceSettingsAudioProcessorEngine(voiceSettingsAudioProcessorEngine.value),
    termsPackages: Array.isArray(voiceSettingsTermsPackages.value) === true ? voiceSettingsTermsPackages.value : [],
    sonioxContext: {
      terms: Array.isArray(voiceSettingsSonioxTerms.value) === true ? voiceSettingsSonioxTerms.value : []
    }
  };
}

function buildAgentPayload() {
  const currentAgentData = getCurrentAgentData();
  const currentConfigurations = currentAgentData.configurations !== undefined && currentAgentData.configurations !== null && typeof currentAgentData.configurations === 'object' ? currentAgentData.configurations : {};
  const nextVoiceSettings = buildVoiceSettingsPayload();
  const nextRoutes = ivrRoutes.value.map((item) => normalizeRoute(item));
  const generatedDescription = buildGeneratedDescription();
  return {
    aiAgentName: agentName.value.trim(),
    divisionIds: preservedDivisionIds.value,
    aiAgentStatus: agentActive.value === true ? 'active' : 'inactive',
    agentType: preservedAgentType.value,
    agentMode: preservedAgentMode.value,
    agentShortDescription: agentShortDescription.value.trim(),
    agentData: {
      ...currentAgentData,
      description: generatedDescription,
      rules: rules.value.filter((item) => typeof item === 'string' && item.trim() !== '').map((item) => item.trim()),
      language: preservedAgentLanguage.value,
      setupType: 'ivr',
      voiceSettings: nextVoiceSettings,
      configurations: {
        ...currentConfigurations,
        deploymentChannels: ['Phone'],
        phoneVoiceId: nextVoiceSettings.voiceId,
        voiceAgent: {
          provider: 'openai-realtime',
          voice: nextVoiceSettings.voiceId,
          extraPrompt: generatedDescription
        }
      },
      ivrSettings: {
        generalTransferSentence: generalTransferSentence.value.trim(),
        connectionMode: connectionMode.value,
        communicationPhoneSourceId: connectionMode.value === 'sip' && selectedPhoneSourceId.value === null ? null : selectedPhoneSourceId.value,
        communicationPhoneNumber: connectionMode.value === 'sip' && selectedPhoneSourceId.value === null ? '' : phoneDisplayNumber.value,
        sipSettings: {
          directSipEnabled: connectionMode.value === 'sip',
          sipHost: sipHost.value,
          sipPort: sipPort.value,
          autoAnswer: sipAutoAnswer.value === true
        },
        routes: nextRoutes
      }
    }
  };
}

async function syncPhoneBinding(agentId, baseCfg) {
  if (connectionMode.value === 'sip') {
    const currentPhoneBind = await axios.get(`${apiEndpoint}/api/phone/binding/${encodePathSegment(agentId)}`, { ...baseCfg, timeout: undefined });
    const currentBoundId = Number(currentPhoneBind?.data?.sourceId);
    if (Number.isFinite(currentBoundId) === true && currentBoundId > 0) {
      await axios.delete(`${apiEndpoint}/api/phone/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: currentBoundId } });
    }
    return;
  }
  const phoneSourceId = Number(selectedPhoneSourceId.value);
  if (Number.isFinite(phoneSourceId) === true && phoneSourceId > 0) {
    await axios.post(`${apiEndpoint}/api/phone/bind`, { agentId: agentId, sourceId: phoneSourceId }, { ...baseCfg, timeout: undefined });
    return;
  }
  const currentPhoneBind = await axios.get(`${apiEndpoint}/api/phone/binding/${encodePathSegment(agentId)}`, { ...baseCfg, timeout: undefined });
  const currentBoundId = Number(currentPhoneBind?.data?.sourceId);
  if (Number.isFinite(currentBoundId) === true && currentBoundId > 0) {
    await axios.delete(`${apiEndpoint}/api/phone/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: currentBoundId } });
  }
}

async function saveAgent(shouldRedirect) {
  if (validateForm() !== true) {
    return;
  }
  const baseCfg = getSecuredAxiosConfig({ timeout: 0 });
  isSaving.value = true;
  isSavingAndRedirecting.value = shouldRedirect === true;
  try {
    const payload = buildAgentPayload();
    let response;
    if (isEditMode.value === true) {
      const agentId = String(route.query.edit);
      response = await axios.put(`${apiEndpoint}/modifyAIChat/${encodePathSegment(agentId)}`, payload, baseCfg);
      await syncPhoneBinding(agentId, baseCfg);
      toast.success(t('aiPhoneIvrSetup.saved', 'IVR setup updated successfully.'));
    } else {
      response = await axios.post(`${apiEndpoint}/createNewAIChat`, payload, baseCfg);
      const newIdRaw = response?.data?.agentid !== undefined && response?.data?.agentid !== null ? response.data.agentid : (response?.data?.agentId !== undefined && response?.data?.agentId !== null ? response.data.agentId : response.data);
      const newId = newIdRaw !== undefined && newIdRaw !== null ? String(newIdRaw) : '';
      if (newId !== '') {
        await syncPhoneBinding(newId, baseCfg);
      }
      toast.success(t('aiPhoneIvrSetup.created', 'IVR setup created successfully.'));
      if (shouldRedirect !== true && newId !== '') {
        await router.replace(`/aiPhoneIvrSetup?edit=${encodePathSegment(newId)}`);
      }
    }
    if (shouldRedirect === true) {
      await router.push('/aiChatAgents');
      return;
    }
    if (response !== undefined && response !== null && isEditMode.value === true) {
      await loadAgent();
    }
  } catch (error) {
    handleApiError(error, 'saving AI Phone IVR setup');
  } finally {
    isSaving.value = false;
    isSavingAndRedirecting.value = false;
  }
}

function applyVoiceSettings(data) {
  if (data === null || data === undefined || typeof data !== 'object') {
    return;
  }
  voiceSettingsVoiceType.value = normalizeVoiceSettingsVoiceType(data.voiceType);
  voiceSettingsVadEngine.value = normalizeVoiceSettingsVadEngine(data.vadEngine !== undefined ? data.vadEngine : data.vadType);
  voiceSettingsVoiceId.value = typeof data.voiceId === 'string' && data.voiceId.trim() !== '' ? data.voiceId : voiceSettingsVoiceId.value;
  voiceSettingsAccent.value = typeof data.accent === 'string' && data.accent.trim() !== '' ? data.accent : voiceSettingsAccent.value;
  voiceSettingsLanguage.value = typeof data.language === 'string' && data.language.trim() !== '' ? data.language : voiceSettingsLanguage.value;
  voiceSettingsSpeed.value = Number.isFinite(Number(data.speed)) === true ? Number(data.speed) : voiceSettingsSpeed.value;
  voiceSettingsCustomPacingEnabled.value = data.customPacingEnabled === true;
  voiceSettingsPacing.value = Number.isFinite(Number(data.pacing)) === true ? Number(data.pacing) : voiceSettingsPacing.value;
  voiceSettingsDurationStretch.value = Number.isFinite(Number(data.durationStretch)) === true ? Number(data.durationStretch) : voiceSettingsDurationStretch.value;
  voiceSettingsUseDashPauses.value = data.useDashPauses === true;
  voiceSettingsPauseEveryWords.value = Number.isFinite(Number(data.pauseEveryWords)) === true ? Number(data.pauseEveryWords) : voiceSettingsPauseEveryWords.value;
  voiceSettingsDashPauseMs.value = Number.isFinite(Number(data.dashPauseMs)) === true ? Number(data.dashPauseMs) : voiceSettingsDashPauseMs.value;
  voiceSettingsCommaPauseMs.value = Number.isFinite(Number(data.commaPauseMs)) === true ? Number(data.commaPauseMs) : voiceSettingsCommaPauseMs.value;
  voiceSettingsPeriodPauseMs.value = Number.isFinite(Number(data.periodPauseMs)) === true ? Number(data.periodPauseMs) : voiceSettingsPeriodPauseMs.value;
  voiceSettingsIdleAutoResponseEnabled.value = data.idleAutoResponseEnabled !== false;
  voiceSettingsStartingMessageIdleAutoResponseSeconds.value = Number.isFinite(Number(data.startingMessageIdleAutoResponseSeconds)) === true && Number(data.startingMessageIdleAutoResponseSeconds) >= 1 ? Math.min(300, Number(data.startingMessageIdleAutoResponseSeconds)) : 6;
  voiceSettingsIdleAutoResponseSeconds.value = Number.isFinite(Number(data.idleAutoResponseSeconds)) === true && Number(data.idleAutoResponseSeconds) >= 1 ? Math.min(300, Number(data.idleAutoResponseSeconds)) : 3;
  voiceSettingsToolUseAutoResponseSeconds.value = Number.isFinite(Number(data.toolUseAutoResponseSeconds)) === true && Number(data.toolUseAutoResponseSeconds) >= 0 ? Math.min(300, Number(data.toolUseAutoResponseSeconds)) : 2;
  voiceSettingsTone.value = typeof data.tone === 'string' && data.tone.trim() !== '' ? data.tone : voiceSettingsTone.value;
  voiceSettingsMode.value = typeof data.mode === 'string' && data.mode.trim() !== '' ? data.mode : voiceSettingsMode.value;
  voiceSettingsCancellingMode.value = normalizeVoiceSettingsCancellingMode(data.cancellingMode !== undefined ? data.cancellingMode : data.cancelingMode);
  voiceSettingsAudioProcessorEngine.value = normalizeVoiceSettingsAudioProcessorEngine(data.audioProcessorEngine !== undefined ? data.audioProcessorEngine : data.audioProcessor);
  voiceSettingsTermsPackages.value = Array.isArray(data.termsPackages) === true ? data.termsPackages.filter((item) => typeof item === 'string' && item.trim() !== '') : [];
  voiceSettingsSonioxTerms.value = Array.isArray(data.sonioxContext?.terms) === true ? data.sonioxContext.terms.filter((item) => typeof item === 'string' && item.trim() !== '') : [];
}

function processLoadedAgentData(agentRecord) {
  loadedAgentRecord.value = agentRecord;
  agentName.value = typeof agentRecord.agentname === 'string' ? agentRecord.agentname : '';
  agentShortDescription.value = typeof agentRecord.agentShortDescription === 'string' ? agentRecord.agentShortDescription : '';
  agentActive.value = typeof agentRecord.agentstatus === 'string' ? agentRecord.agentstatus.toLowerCase() === 'active' : true;
  preservedDivisionIds.value = Array.isArray(agentRecord.divisionIds) === true ? agentRecord.divisionIds.filter((item) => Number.isInteger(item) === true) : [];
  preservedAgentType.value = typeof agentRecord.agenttype === 'string' && agentRecord.agenttype.trim() !== '' ? agentRecord.agenttype : 'PublicAgent';
  preservedAgentMode.value = typeof agentRecord.agentMode === 'string' && agentRecord.agentMode.trim() !== '' ? agentRecord.agentMode : 'Agentic';
  const agentData = agentRecord.agentdata !== undefined && agentRecord.agentdata !== null && typeof agentRecord.agentdata === 'object' ? agentRecord.agentdata : {};
  const configurations = agentData.configurations !== undefined && agentData.configurations !== null && typeof agentData.configurations === 'object' ? agentData.configurations : {};
  preservedAgentLanguage.value = typeof agentData.language === 'string' && agentData.language.trim() !== '' ? agentData.language : 'auto';
  agentLanguage.value = preservedAgentLanguage.value;
  rules.value = Array.isArray(agentData.rules) === true ? agentData.rules.filter((item) => typeof item === 'string' && item.trim() !== '').map((item) => item.trim()) : [];
  const ivrSettings = agentData.ivrSettings !== undefined && agentData.ivrSettings !== null && typeof agentData.ivrSettings === 'object' ? agentData.ivrSettings : {};
  generalTransferSentence.value = typeof ivrSettings.generalTransferSentence === 'string' ? ivrSettings.generalTransferSentence : '';
  if (Array.isArray(ivrSettings.routes) === true) {
    ivrRoutes.value = ivrSettings.routes.map((item) => normalizeRoute(item));
  } else {
    ivrRoutes.value = [];
  }
  const sipSettings = ivrSettings.sipSettings !== undefined && ivrSettings.sipSettings !== null && typeof ivrSettings.sipSettings === 'object' ? ivrSettings.sipSettings : {};
  if (typeof sipSettings.sipHost === 'string' && sipSettings.sipHost.trim() !== '') {
    sipHost.value = sipSettings.sipHost.trim();
  }
  if (typeof sipSettings.sipPort === 'string' && sipSettings.sipPort.trim() !== '') {
    sipPort.value = sipSettings.sipPort.trim();
  }
  if (sipSettings.autoAnswer !== undefined) {
    sipAutoAnswer.value = sipSettings.autoAnswer === true;
  }
  if (ivrSettings.connectionMode === 'sip') {
    connectionMode.value = 'sip';
  } else if (sipSettings.directSipEnabled === true) {
    connectionMode.value = 'sip';
  } else if (Number.isFinite(Number(ivrSettings.communicationPhoneSourceId)) === true && Number(ivrSettings.communicationPhoneSourceId) > 0) {
    connectionMode.value = 'phone';
  } else {
    connectionMode.value = 'sip';
  }
  if (Number.isFinite(Number(ivrSettings.communicationPhoneSourceId)) === true) {
    selectedPhoneSourceId.value = Number(ivrSettings.communicationPhoneSourceId);
  }
  if (typeof ivrSettings.communicationPhoneNumber === 'string') {
    phoneDisplayNumber.value = ivrSettings.communicationPhoneNumber;
  }
  if (typeof configurations.phoneVoiceId === 'string' && configurations.phoneVoiceId.trim() !== '') {
    voiceSettingsVoiceId.value = configurations.phoneVoiceId;
  }
  applyVoiceSettings(agentData.voiceSettings !== undefined ? agentData.voiceSettings : null);
}

async function loadPhoneBinding(agentId) {
  try {
    const cfg = getSecuredAxiosConfig(null);
    const response = await axios.get(`${apiEndpoint}/api/phone/binding/${encodePathSegment(agentId)}`, cfg);
    if (response.data !== null && response.data !== undefined) {
      selectedPhoneSourceId.value = response.data.sourceId !== undefined ? response.data.sourceId : selectedPhoneSourceId.value;
      const phoneNumber = typeof response.data.number === 'string' ? response.data.number : (typeof response.data.externalNumber === 'string' ? response.data.externalNumber : (typeof response.data.display === 'string' ? response.data.display : ''));
      if (phoneNumber !== '') {
        phoneDisplayNumber.value = phoneNumber;
      }
    }
  } catch (error) {
    if (phoneDisplayNumber.value === '') {
      phoneDisplayNumber.value = '';
    }
  }
}

async function loadAgent() {
  if (isEditMode.value !== true) {
    initializing.value = false;
    return;
  }
  try {
    const agentId = String(route.query.edit);
    const cfg = getSecuredAxiosConfig(null);
    const response = await axios.get(`${apiEndpoint}/getAIChat/${encodePathSegment(agentId)}`, cfg);
    processLoadedAgentData(response.data);
    await loadPhoneBinding(agentId);
  } catch (error) {
    handleApiError(error, 'loading AI Phone IVR setup');
  } finally {
    initializing.value = false;
  }
}

onMounted(async () => {
  const requestedSetupType = Array.isArray(route.query.setupType) === true ? route.query.setupType[0] : route.query.setupType;
  if (requestedSetupType === 'full') {
    switchView('full');
    return;
  }
  if (requestedSetupType === 'light') {
    switchView('light');
    return;
  }
  await loadAgent();
  viewMode.value = 'ivr';
});
</script>
